import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { Checkbox } from "@dropbox/dig-components/controls";
import { Overlay } from "@dropbox/dig-components/overlay";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { Typeahead } from "@dropbox/dig-components/typeahead";
import { UIIcon } from "@dropbox/dig-icons";
import { SearchLine } from "@dropbox/dig-icons/assets";
import { KeyCodes } from "@mirage/shared/util/constants";
import i18n from "@mirage/translations";
import { useEffect, useMemo, useRef } from "react";
import styles from "./ChannelFilterTypeahead.module.css";
import { useChannelSearch } from "./useChannelSearch";
import { PeopleSearchStatus } from "./usePeopleSearch";

import type { TextInputRefObject } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import type { ChannelForFilter } from "@mirage/shared/search/search-filters";
import { useTranslation } from "react-i18next";

type ChannelFilterTypeaheadProps = {
  selectedChannels: ChannelForFilter[];
  onChannelSelection: (channel: ChannelForFilter) => void;
  onClose: () => void;
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLButtonElement>;
};

export const ChannelFilterTypeahead = ({
  selectedChannels,
  onChannelSelection,
  onClose,
  isOpen,
  anchorRef,
}: ChannelFilterTypeaheadProps) => {
  const { t } = useTranslation();
  const {
    channelQuery,
    setChannelQuery,
    isZeroQuery,
    channels,
    searchStatus: channelSearchStatus,
  } = useChannelSearch();

  const inputRef = useRef<TextInputRefObject>(null);
  const selectedChannelsByName = useMemo(
    () =>
      selectedChannels.reduce(
        (acc, channel) => {
          acc[channel.name] = channel;
          return acc;
        },
        {} as Record<string, ChannelForFilter>,
      ),
    [selectedChannels],
  );
  const channelsToDisplay = useMemo(() => {
    if (isZeroQuery) {
      // dedupe selected channels from channel results and
      // show them at the top when not searching, so they can be unselected
      const channelResultsToDisplay = channels.filter(
        (channel) => !selectedChannelsByName[channel.name],
      );
      return [...selectedChannels, ...channelResultsToDisplay];
    }
    return channels;
  }, [channels, isZeroQuery, selectedChannels, selectedChannelsByName]);

  useEffect(() => {
    setChannelQuery("");
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChannelQuery(e.target.value);
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case KeyCodes.escape:
      case KeyCodes.tab:
        e.preventDefault();
        e.stopPropagation();
        onClose();
        anchorRef?.current?.focus();
        break;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ClickOutside isActive shouldPropagateMouseEvents onClickOutside={onClose}>
      <Overlay
        offsetDistance={4}
        anchorRef={anchorRef}
        placement="bottom-start"
        onKeyDown={handleOverlayKeyDown}
        auto
      >
        <Typeahead.Wrapper onSelection={onChannelSelection} omitOverlay>
          {({ getTriggerProps, getContentProps }) => (
            <div className={styles.container}>
              <div className={styles.input}>
                <TextInput
                  {...getTriggerProps({
                    onChange: handleInputChange,
                  })}
                  ref={inputRef}
                  value={channelQuery}
                  placeholder={i18n.t("channel_search_input_placeholder")}
                  aria-label={i18n.t("channel_search_empty")}
                  isTransparent={true}
                  style={{ paddingLeft: "var(--dig-spacing__micro__xsmall)" }}
                  withLeftAccessory={
                    <UIIcon
                      src={SearchLine}
                      color="var(--dig-color__text__subtle)"
                    />
                  }
                />
              </div>

              <Typeahead.Container
                {...getContentProps()}
                // force the results open, regardless of whether the input is focused
                open
                // Typeahead's isEmptyQuery actually means "should we display the emptyPrompt".
                // We show the empty prompt if no results are found, not if the query is empty
                isEmptyQuery={
                  !isZeroQuery &&
                  channels.length === 0 &&
                  channelSearchStatus !== PeopleSearchStatus.LOADING
                }
                loading={channelSearchStatus === PeopleSearchStatus.LOADING}
                emptyPrompt={
                  <Typeahead.EmptyPrompt
                    placeholderText={i18n.t("channel_search_no_results")}
                  />
                }
              >
                <Box as="div" className={styles.channelResultsContainer}>
                  <Box className={styles.channelResultsHeader}>
                    <Text variant="label" size="small" color="subtle" isBold>
                      {t("relevant_channels")}
                    </Text>
                  </Box>
                  {channelsToDisplay.map((channel) => (
                    <Typeahead.Row
                      className={styles.channelResultRow}
                      key={channel.name}
                      value={channel}
                      withTitle={channel.name}
                      withLeftAccessory={
                        <Checkbox
                          checked={!!selectedChannelsByName[channel.name]}
                          readOnly
                          tabIndex={-1}
                        />
                      }
                    />
                  ))}
                </Box>
              </Typeahead.Container>
            </div>
          )}
        </Typeahead.Wrapper>
      </Overlay>
    </ClickOutside>
  );
};
